import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { agileUren, fmtDag, fmtUren, geplandeUren } from '@ep/core';
import { PROJECT_STATUS, useData, useMag } from '../lib/data';
import { Balk, Badge, Laden, MultiSelect, Veld } from '../ui/ui';
import { ProjectForm } from './ProjectForm';
import { Zijpaneel } from '../ui/Shell';
import { useKolomVelden, VeldCel } from '../ui/EigenVelden';
import { Icoon } from '../ui/Icoon';

type Sortering = 'klant' | 'nummer' | 'livegang' | 'rest';

export function Projecten() {
  const { data: d } = useData();
  const mag = useMag();
  const kolommen = useKolomVelden('PROJECT');
  const nav = useNavigate();
  const [zoek, setZoek] = useState('');
  const [klant, setKlant] = useState<string[]>([]);
  const [status, setStatus] = useState<string[]>([]);
  const [pm, setPm] = useState<string[]>([]);
  const [exp, setExp] = useState<string[]>([]);
  const [eng, setEng] = useState<string[]>([]);
  const [afgerond, setAfgerond] = useState(false);
  const [sort, setSort] = useState<Sortering>('klant');
  const [zp, setZp] = useSearchParams();
  const [nieuw, setNieuw] = useState(zp.get('nieuw') === '1');
  const [kijk, setKijk] = useState<string | null>(null);

  const rijen = useMemo(() => {
    if (!d) return [];
    const z = zoek.toLowerCase();
    return d.projecten
      .filter((p) => afgerond || p.status !== 'AFGEROND')
      .filter((p) => !z || [p.nummer, p.naam, p.klant, p.pmNaam, p.locatie, ...Object.values(p.velden ?? {}).map((v) => (typeof v === 'string' ? v : null))].some((x) => x?.toLowerCase().includes(z)))
      .filter((p) => !klant.length || klant.includes(p.klant ?? ''))
      .filter((p) => !status.length || status.includes(p.status))
      .filter((p) => !pm.length || pm.includes(p.pmNaam ?? ''))
      .filter((p) => !exp.length || p.expertiseBudget.some((b) => exp.includes(b.naam)))
      .filter((p) => !eng.length || d.taken.some((t) => t.projectId === p.id && eng.includes(t.engineerId ?? '')))
      .map((p) => {
        const gepland = geplandeUren(d.taken.filter((t) => t.projectId === p.id)) + agileUren(d.takenpakketten.filter((t) => t.projectId === p.id)).besteed + (p.besteedLosMin ?? 0) / 60;
        const budget = p.urenBudget ?? p.expertiseBudget.reduce((s, b) => s + b.uren, 0);
        return { p, gepland, budget, rest: budget - gepland };
      })
      .sort((a, b) =>
        sort === 'nummer'
          ? a.p.nummer.localeCompare(b.p.nummer)
          : sort === 'livegang'
            ? (a.p.livegang ?? '9999').localeCompare(b.p.livegang ?? '9999')
            : sort === 'rest'
              ? b.rest - a.rest
              : (a.p.klant ?? '').localeCompare(b.p.klant ?? '') || a.p.nummer.localeCompare(b.p.nummer),
      );
  }, [d, zoek, klant, status, pm, exp, eng, afgerond, sort]);

  if (!d) return <Laden />;
  const uniek = (xs: (string | null | undefined)[]) => [...new Set(xs.filter(Boolean) as string[])].sort().map((x) => ({ waarde: x, label: x }));

  return (
    <div className="pagina">
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 280px) 1fr', gap: 16, alignItems: 'start' }}>
        <div className="paneel kolom">
          <h1>Projecten</h1>
          {mag('projecten.schrijven') && (
            <button className="knop primair" onClick={() => setNieuw(true)}>
              + Nieuw project
            </button>
          )}
          <Veld label="Zoeken">
            <input value={zoek} onChange={(e) => setZoek(e.target.value)} placeholder="Nummer, naam, klant, locatie" />
          </Veld>
          <Veld label="Klant">
            <MultiSelect opties={uniek(d.projecten.map((p) => p.klant))} waarden={klant} onChange={setKlant} placeholder="Alle klanten" />
          </Veld>
          <Veld label="Status">
            <MultiSelect opties={Object.entries(PROJECT_STATUS).map(([waarde, label]) => ({ waarde, label }))} waarden={status} onChange={setStatus} placeholder="Alle statussen" />
          </Veld>
          <Veld label="Projectmanager">
            <MultiSelect opties={uniek(d.projecten.map((p) => p.pmNaam))} waarden={pm} onChange={setPm} placeholder="Alle projectmanagers" />
          </Veld>
          <Veld label="Expertise">
            <MultiSelect opties={d.expertises.map((x) => ({ waarde: x.naam, label: x.naam }))} waarden={exp} onChange={setExp} placeholder="Alle expertises" />
          </Veld>
          <Veld label="Engineer (heeft taken in het project)">
            <MultiSelect opties={d.engineers.map((x) => ({ waarde: x.id, label: x.naam }))} waarden={eng} onChange={setEng} placeholder="Alle engineers" />
          </Veld>
          <label className="check">
            <input type="checkbox" checked={afgerond} onChange={(e) => setAfgerond(e.target.checked)} /> Ook afgeronde projecten tonen
          </label>
          <Veld label="Sorteren op">
            <select value={sort} onChange={(e) => setSort(e.target.value as Sortering)}>
              <option value="klant">Klant</option>
              <option value="nummer">Projectnummer</option>
              <option value="livegang">Livegang</option>
              <option value="rest">Meeste rest</option>
            </select>
          </Veld>
        </div>
        <div className="paneel" style={{ padding: 0, overflow: 'auto' }}>
          <table className="tabel">
            <thead>
              <tr>
                <th>Project</th>
                <th>Klant</th>
                <th>Projectmanager</th>
                <th className="rechts">Budget</th>
                <th className="rechts">Ingepland</th>
                <th className="rechts">Rest</th>
                <th>Start – oplevering · livegang</th>
                {kolommen.map((k) => <th key={k.id}>{k.naam}</th>)}
              </tr>
            </thead>
            <tbody>
              {rijen.map(({ p, gepland, budget, rest }) => {
                const laat = p.prognose && p.livegang && p.prognose > p.livegang;
                return (
                  <tr key={p.id} className={`klik ${kijk === p.id ? 'gekozen' : ''}`} onClick={() => setKijk(p.id)} onDoubleClick={() => nav(`/projecten/${p.id}`)}>
                    <td>
                      <div className="rij">
                        <b>{p.nummer}</b>
                        <Badge toon={p.status === 'IN_UITVOERING' ? 'groen' : p.status === 'ON_HOLD' ? 'oranje' : p.status === 'AFGEROND' ? 'grijs' : 'accent'}>{PROJECT_STATUS[p.status]}</Badge>
                      </div>
                      <Link className="klein project-naam" to={`/projecten/${p.id}`} onClick={(e) => e.stopPropagation()}>{p.naam}</Link>
                    </td>
                    <td>{p.klant}</td>
                    <td>{p.pmNaam}</td>
                    <td className="rechts">{fmtUren(budget)}u</td>
                    <td className="rechts">{fmtUren(gepland)}u</td>
                    <td className="rechts" style={{ minWidth: 90 }}>
                      <div className={rest < 0 ? 'vet' : ''} style={{ color: rest < 0 ? 'var(--rood)' : undefined }}>
                        {fmtUren(rest)}u
                      </div>
                      <Balk waarde={gepland} max={budget} />
                    </td>
                    <td className="klein">
                      <div>{p.start || p.oplevering ? `${fmtDag(p.start, 'jaar')} – ${fmtDag(p.oplevering, 'jaar')}` : 'geen datums'}</div>
                      {p.livegang && <div style={{ color: laat ? 'var(--rood)' : 'var(--groen)' }}>◆ live {fmtDag(p.livegang, 'jaar')}{p.prognose ? ` · prog. ${fmtDag(p.prognose, 'datum')}` : ''}</div>}
                    </td>
                    {kolommen.map((k) => <td key={k.id} className="klein"><VeldCel def={k} waarden={p.velden} /></td>)}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {!rijen.length && <div className="leeg">Geen projecten gevonden.</div>}
        </div>
      </div>
      {nieuw && <ProjectForm onSluit={() => { setNieuw(false); if (zp.get('nieuw')) setZp({}, { replace: true }); }} onOpgeslagen={(id) => nav(`/projecten/${id}`)} />}
      {kijk && (() => {
        const rij = rijen.find((x) => x.p.id === kijk) ?? null;
        if (!rij) return null;
        const { p, gepland, budget, rest } = rij;
        const laat = !!(p.prognose && p.livegang && p.prognose > p.livegang);
        const wps = d.werkpakketten.filter((w) => w.projectId === p.id);
        const team = new Set(d.taken.filter((t) => t.projectId === p.id && t.engineerId).map((t) => t.engineerId));
        return (
          <Zijpaneel
            onSluit={() => setKijk(null)}
            titel={<><div className="klein mute">{p.nummer} · {p.klant}</div><h2 style={{ margin: 0 }}>{p.naam}</h2></>}
            voet={<>
              <Link className="knop primair" to={`/projecten/${p.id}`}><Icoon naam="arrow-right" maat={16} /> Openen</Link>
              <Link className="knop" to={`/projecten/${p.id}/werkpakketten`}>Werkzaamheden</Link>
              <Link className="knop" to={`/projecten/${p.id}/planning`}>Planning</Link>
              <Link className="knop" to={`/projecten/${p.id}/rapport`}>Rapport</Link>
            </>}
          >
            <div className="kolom" style={{ gap: 14 }}>
              <div className="rij wrap" style={{ gap: 6 }}>
                <Badge toon={p.status === 'IN_UITVOERING' ? 'groen' : p.status === 'ON_HOLD' ? 'oranje' : p.status === 'AFGEROND' ? 'grijs' : 'accent'}>{PROJECT_STATUS[p.status]}</Badge>
                {p.livegang && <Badge toon={laat ? 'rood' : 'groen'}>{laat ? 'prognose na livegang' : p.prognose ? 'op tijd' : 'geen prognose'}</Badge>}
              </div>
              <div>
                <div className="rij"><span className="groei klein mute">Uren</span><b>{fmtUren(gepland)} / {fmtUren(budget)} u</b></div>
                <Balk waarde={gepland} max={budget} />
                <div className="klein" style={{ color: rest < 0 ? 'var(--rood)' : 'var(--mute)', marginTop: 4 }}>{rest < 0 ? `${fmtUren(-rest)} uur over budget` : `nog ${fmtUren(rest)} uur in te plannen`}</div>
              </div>
              <dl className="zp-gegevens">
                <dt>Projectmanager</dt><dd>{p.pmNaam ?? '–'}</dd>
                <dt>Start</dt><dd>{fmtDag(p.start, 'jaar') || '–'}</dd>
                <dt>Oplevering</dt><dd>{fmtDag(p.oplevering, 'jaar') || '–'}</dd>
                <dt>Livegang</dt><dd>{fmtDag(p.livegang, 'jaar') || '–'}</dd>
                <dt>Prognose</dt><dd style={{ color: laat ? 'var(--rood)' : undefined }}>{fmtDag(p.prognose, 'jaar') || '–'}</dd>
                <dt>Werkpakketten</dt><dd>{wps.length}</dd>
                <dt>Team</dt><dd>{team.size} engineer{team.size === 1 ? '' : 's'}</dd>
                <dt>Locatie</dt><dd>{p.geoNaam ?? p.locatie ?? '–'}</dd>
              </dl>
              {p.omschrijving && <p className="klein mute" style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{p.omschrijving}</p>}
            </div>
          </Zijpaneel>
        );
      })()}
    </div>
  );
}
